import {Component, effect, input, signal} from '@angular/core';
import {UtilityService} from '../../../../shared/services/utility/utility.service';
import {BorService} from '../../../../sdk/bor-api';
import {PscListResponse} from '../../../../sdk/bor-api/models/models/psc-list-response';
import * as FileServer from 'file-saver';
import {ToastrService} from '../../../../shared/services/toast/toastr.service';
import {finalize} from 'rxjs';
import {getCompanyTypesById} from '../../../../utils/datasets/business-classifications.dataset';

@Component({
  selector: 'bor-company-psc-details',
  standalone: false,
  templateUrl: './company-psc-details.component.html',
  styleUrl: './company-psc-details.component.scss',
})
export class CompanyPscDetailsComponent {
  companyId = input<number>(NaN, {transform: v => Number(`${v}`?.trim())});

  pscDetails = signal<PscListResponse | null>(null);

  activePscId = signal<number>(-1);

  downloading = signal({pdf: false, json: false});

  constructor(
    public utility:UtilityService,
    private bor:BorService,
    private toastr: ToastrService,
  ) {
    effect(() => {
      if (!this.companyId() || isNaN(this.companyId())) {
        utility.goBack();
        return;
      }
      this.getCompanyDetails()
    });
  }

  get companyType(){
    return getCompanyTypesById(this.pscDetails()?.companyTypeFk)?.name?.replaceAll("_", " ") || '';
  }

  getCompanyDetails(){
    this.bor.apiV1BorCompaniesCompanyIdPscGet({ companyId: this.companyId() })
      .subscribe({
        next: (res) => this.pscDetails.set(res),
        error: (err) => {
          this.toastr.showDanger(err?.error?.message || 'Failed to find Company PSC details');
          this.utility.goBack();
        }
      })
  }

  setActivePscId(id:number){
    this.activePscId.update(prev => (prev === id) ? -1 : id);
  }

  downloadAsPdf(){
    this.downloading.update(v => ({...v, pdf: true}));
    this.bor.apiV1BorCompaniesCompanyIdPscReportPdfGet({ companyId: this.companyId() })
      .pipe(finalize(() => {
        this.downloading.update(v => ({...v, pdf: false}));
      }))
      .subscribe({
        next: (res) => {
          FileServer.saveAs(res, `PSC Report for ${this.pscDetails()?.approvedName} (RC - ${this.pscDetails()?.rcNumber}).pdf`);
        },
        error: (err) =>
          this.toastr.showDanger(err?.error?.message || 'Failed to download Company PSC details as PDF')
      })
  }

  downloadAsJSON(){
    this.downloading.update(v => ({...v, json: true}));
    this.bor.apiV1BorCompaniesCompanyIdPscReportJsonGet({ companyId: this.companyId() })
      .pipe(finalize(() => {
        this.downloading.update(v => ({...v, json: false}));
      }))
      .subscribe({
        next: (res) => {
          // Convert the JavaScript object into formatted JSON text strings
          const jsonString = JSON.stringify(res, null, 2);

          // Create a blob file from the string content
          const blobData = new Blob([jsonString], { type: 'application/json' });
          const downloadUrl = window.URL.createObjectURL(blobData);

          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.target = '_blank';
          anchor.click();

          anchor.target = '_self';
          anchor.download = `PSC Report for ${this.pscDetails()?.approvedName} (RC - ${this.pscDetails()?.rcNumber}).json`;
          anchor.click();

          window.URL.revokeObjectURL(downloadUrl);
        },
        error: (err) =>
          this.toastr.showDanger(err?.error?.message || 'Failed to download Company PSC details as JSON')
      })
  }


}
